import * as React from 'react';

import { Badge, DataGridColumn, LocalizedDate } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';
import {
  CounterpartyGroupRecord,
  OPERATION_TYPE_COLORS,
} from '../../../types/pricing';

type Props = {
  onRowClick: (row: CounterpartyGroupRecord) => void;
};

export const useCounterpartyGroupColumns = ({ onRowClick }: Props) => {
  const t = useTranslations();

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: CounterpartyGroupRecord }) => {
      onRowClick(row);
    },
    [onRowClick],
  );

  const columns: DataGridColumn<CounterpartyGroupRecord>[] = React.useMemo(
    () => [
      {
        key: 'name',
        header: t.pricing.counterpartyGroups.table.columns.name,
        sortable: true,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left">{row.name}</div>
        ),
      },
      {
        key: 'operationType',
        header: t.pricing.counterpartyGroups.table.columns.operationType,
        sortable: true,
        width: 150,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge color={OPERATION_TYPE_COLORS[row.operationType]} size="small">
            {row.operationType}
          </Badge>
        ),
      },
      {
        key: 'memberCount',
        header: t.pricing.counterpartyGroups.table.columns.memberCount,
        width: 100,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => <div className="text-right">{row.memberCount}</div>,
      },
      {
        key: 'updatedAt',
        header: t.pricing.counterpartyGroups.table.columns.updatedAt,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.updatedAt} />
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: t.pricing.counterpartyGroups.table.columns.created,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
    ],
    [t, handleCellDoubleClick],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
